// Order customization configuration
// Category-specific templates, pairings, and section headers for Tuscan Cove.

// Category → Customization template mapping
// Temperature is required only where doneness is a real kitchen question, so
// steaks is the only category-level temperature mapping. mains, seafood, and
// handhelds fall through to "default" (their plates are fish, fried, shrimp,
// chicken, or smash-style items with a standard prep); the few genuinely
// temp-cooked plates outside steaks opt back in via ITEM_TEMPLATE_OVERRIDES.
export const CATEGORY_TEMPLATES: Record<string, "temperature" | "pasta" | "pizza" | "salad" | "brunch" | "default"> = {
  steaks: "temperature",
  pastas: "pasta",
  pizza: "pizza",
  salads: "salad",
  brunch_salads: "salad",
  brunch_mains: "brunch",
  brunch_benedicts: "brunch",
  brunch_sweets: "brunch",
  catering_trays: "default",
  // All others → "default"
};

// Item-level template overrides for plates whose prep question does not follow
// their category. Keys are normalized item names: leading "*" stripped, lowercased.
// Evidence: menu:getAvailable for tuscan-cove-southern-highlands, 2026-06-10.
export const ITEM_TEMPLATE_OVERRIDES: Record<string, CategoryTemplate> = {
  // Grilled steak plate in mains; doneness is a real kitchen question.
  "carne asada": "temperature",
  // Steak cooked to order in brunch_mains; same doneness question as a steak plate.
  "steak and eggs": "temperature",
  // Deliberately NOT overridden, so they never require a temperature:
  // - Fish & Chips, *Salmon Burger, *Blackened Salmon, Grilled Shrimp Skewers:
  //   fish, shrimp, and fried items have one standard prep.
  // - *Double Smash Burger: smash patties are cooked through.
  // - Chicken Parmesan, Eggplant Parmesan, Chicken Prosciutto Caprese, and other
  //   chicken/veal/parm/milanese plates: no doneness to choose.
  // - Chicken or Steak Cheese Bomber: thin-sliced griddle steak; the choice is
  //   protein, not doneness.
};

// Category badge icons and labels.
export const CATEGORY_BADGES: Record<string, { icon: string; label: string; color: string }> = {
  steaks: { icon: "🔥", label: "Grilled", color: "text-orange-500" },
  mains: { icon: "🍽️", label: "Entrée", color: "text-amber-600" },
  seafood: { icon: "🐟", label: "Fresh Catch", color: "text-blue-500" },
  pastas: { icon: "🍝", label: "Pasta", color: "text-red-500" },
  pizza: { icon: "🍕", label: "Wood-Fired", color: "text-orange-600" },
  salads: { icon: "🥗", label: "Fresh", color: "text-green-500" },
  starters: { icon: "✨", label: "Antipasti", color: "text-amber-500" },
  desserts: { icon: "🍰", label: "Dolci", color: "text-pink-500" },
  brunch_mains: { icon: "☀️", label: "Brunch", color: "text-yellow-500" },
  brunch_benedicts: { icon: "🍳", label: "Eggs", color: "text-yellow-600" },
  handhelds: { icon: "🍔", label: "Handhelds", color: "text-amber-700" },
};

// Get category badge info
export function getCategoryBadge(category: string): { icon: string; label: string; color: string } | null {
  return CATEGORY_BADGES[category] || null;
}

// Temperature options (abbreviated)
export const TEMPERATURE_OPTIONS = [
  { value: "MR", label: "MR", description: "Medium Rare" },
  { value: "Med", label: "Med", description: "Medium", isRecommended: true },
  { value: "MW", label: "MW", description: "Medium Well" },
  { value: "Well", label: "Well", description: "Well Done" },
] as const;

// Sauce amount options (pasta)
export const SAUCE_OPTIONS = [
  { value: "light", label: "Light" },
  { value: "regular", label: "Regular", isDefault: true },
  { value: "extra", label: "Extra" },
] as const;

// Crust options (pizza)
export const CRUST_OPTIONS = [
  { value: "regular", label: "Regular", isDefault: true },
  { value: "well-done", label: "Well-done" },
  { value: "light", label: "Light" },
] as const;

// Tuscan Cove printed pizza toppings.
// Source menu note: large +$3.00, small +$1.50 each.
export const PIZZA_TOPPING_OPTIONS = [
  { value: "mushrooms", label: "Mushrooms" },
  { value: "onions", label: "Onions" },
  { value: "tomatoes", label: "Tomatoes" },
  { value: "olives", label: "Olives" },
  { value: "bell-peppers", label: "Bell Peppers" },
  { value: "jalapeno", label: "Jalapeno" },
  { value: "pepperoncini", label: "Pepperoncini" },
  { value: "anchovies", label: "Anchovies" },
  { value: "sausage", label: "Sausage" },
  { value: "prosciutto", label: "Prosciutto" },
  { value: "ham", label: "Ham" },
  { value: "pepperoni", label: "Pepperoni" },
  { value: "bacon", label: "Bacon" },
] as const;

export function getPizzaToppingPriceForSize(size: string | null | undefined) {
  return size === "10-inch" ? 150 : 300;
}

// Dressing options (salad)
export const DRESSING_OPTIONS = [
  { value: "on-side", label: "On Side" },
  { value: "light", label: "Light" },
  { value: "regular", label: "Regular", isDefault: true },
] as const;

// Protein add-ons for pasta and salads
export const PROTEIN_OPTIONS = [
  { value: "chicken", label: "Chicken", price: 800 },
  { value: "shrimp", label: "Shrimp", price: 1200 },
  { value: "salmon", label: "Salmon", price: 1400 },
] as const;

// Brunch add-ons
export const BRUNCH_ADDONS = [
  { value: "bacon", label: "Bacon", price: 500 },
  { value: "avocado", label: "Avocado", price: 400 },
  { value: "extra-egg", label: "Extra Egg", price: 300 },
] as const;

// Brunch drinks (for pairings section)
export const BRUNCH_DRINKS = [
  { value: "mimosa-kit", label: "Tableside Mimosa Kit", price: 2500 },
  { value: "bloody-mary", label: "Bloody Mary", price: 1400 },
  { value: "espresso-martini", label: "Espresso Martini", price: 1600 },
] as const;

// Category-specific pairings (simple, logical combinations)
// Note: Item names must match exactly with menu item names in the database
export const PAIRING_ITEMS: Record<string, string[]> = {
  // Pizza pairs well with salads and starters
  pizza: ["Chicken Wings 8pc", "Garlic Cheese Bread", "*Caesar Salad"],
  
  // Handhelds pair well with salads and sides
  handhelds: ["Basket of Fries", "*Caesar Salad", "Chicken Wings 8pc"],
  
  // Steaks pair well with salads and hearty starters
  steaks: ["Wedge Salad", "Garlic Cheese Bread", "Cheese & Charcuterie"],
  
  // Mains pair well with salads and bread
  mains: ["Wedge Salad", "Garlic Cheese Bread", "Cheese & Charcuterie"],
  
  // Seafood pairs well with lighter salads
  seafood: ["Tomato Salad", "Garlic Cheese Bread", "*Shrimp Cocktail"],
  
  // Pastas pair well with salads and bread
  pastas: ["*Caesar Salad", "Garlic Cheese Bread", "Cheese & Charcuterie"],
  
  // Salads pair well with bread and starters
  salads: ["Garlic Cheese Bread", "Chicken Tenders 6pc", "Basket of Fries"],
  
  // Brunch items
  brunch: ["Bacon", "Sausage", "Potato Hash"],
  brunch_mains: ["Bacon", "Sausage", "Potato Hash"],
  brunch_benedicts: ["Bacon", "Sausage", "Egg"],
  brunch_sweets: ["Bacon", "Sausage", "Egg"],
  desserts: ["Artisanal Gelato", "Cookie Sundae", "Blueberry Cheesecake"],
};

// Contextual section headers per category
export const SECTION_HEADERS: Record<string, { title: string; subtitle?: string }> = {
  steaks: { title: "Goes well with", subtitle: "Popular pairings" },
  mains: { title: "Goes well with" },
  seafood: { title: "Goes well with" },
  pastas: { title: "Goes well with" },
  pizza: { title: "Goes well with" },
  salads: { title: "Goes well with" },
  handhelds: { title: "Goes well with" },
  brunch: { title: "Brunch drinks" },
  brunch_mains: { title: "Brunch drinks" },
  brunch_benedicts: { title: "Brunch drinks" },
  brunch_sweets: { title: "Brunch drinks" },
  desserts: { title: "Save room for" },
};

// Special layouts per category
export const CATEGORY_LAYOUTS: Record<string, "list" | "grid"> = {
  steaks: "grid", // 2-column grid for steak sides
  // All others default to "list"
};

// Template type
export type CategoryTemplate = "temperature" | "pasta" | "pizza" | "salad" | "brunch" | "default";

// Get template for a category
export function getTemplateForCategory(category: string): CategoryTemplate {
  return CATEGORY_TEMPLATES[category] || "default";
}

/** Strip the kitchen leading "*" marker for guest-facing labels. */
export function guestFacingMenuItemName(name: string): string {
  return name.replace(/^\*+\s*/, "").trim();
}

// Normalize a menu item name for override lookup: strip the menu's leading "*"
// marker (raw/undercooked consumer advisory), trim, and lowercase.
export function normalizeItemName(name: string): string {
  return guestFacingMenuItemName(name).toLowerCase();
}

// Get template for a specific item: item-level override first, then category.
export function getTemplateForItem(item: { name: string; category: string; restaurantId?: string; locationId?: string }): CategoryTemplate {
  // Roma choices/prices come exclusively from item-linked Convex modifiers.
  // Never suppress them in favor of Tuscan's generic pasta/salad add-ons.
  if (item.restaurantId === "roma-kitchen" || item.locationId?.startsWith("roma-kitchen-")) return "default";
  return ITEM_TEMPLATE_OVERRIDES[normalizeItemName(item.name)] ?? getTemplateForCategory(item.category);
}

// Get pairing items for a category
export function getPairingsForCategory(category: string): string[] {
  return PAIRING_ITEMS[category] || [];
}

// Get section header for a category
export function getSectionHeader(category: string): { title: string; subtitle?: string } | null {
  return SECTION_HEADERS[category] || null;
}

// Get layout for a category
export function getLayoutForCategory(category: string): "list" | "grid" {
  return CATEGORY_LAYOUTS[category] || "list";
}
